import React from "react";

import { fromSiteList, toSiteList } from "../utils";

import type {
  CrawlerPreferenceData,
  CrawlerPreferenceItem,
  CrawlerPreferenceType,
  DriveInfo,
} from "../types/crawlerPreference";

interface UseCrawlerPreferencesStateProps {
  crawlerPreferenceData?: CrawlerPreferenceData;
}

const NO_GROUPS: CrawlerPreferenceType[] = [];

export type CrawlerPreferencesState = ReturnType<
  typeof useCrawlerPreferencesState
>;

export function useCrawlerPreferencesState({
  crawlerPreferenceData,
}: UseCrawlerPreferencesStateProps) {
  const [preferences, setPreferences] = React.useState<CrawlerPreferenceType[]>(
    [],
  );
  const [activeTab, setActiveTab] = React.useState<string>("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedDriveId, setSelectedDriveId] = React.useState<string>("");

  const groups = crawlerPreferenceData?.preferences ?? NO_GROUPS;
  const siteList = React.useMemo(() => toSiteList(groups), [groups]);

  // Determine if we have multiple drives
  const hasDriveMetadata = React.useMemo(
    () => siteList.some((pref) => pref.drive_id !== undefined),
    [siteList],
  );

  // Extract unique drives from preferences
  const availableDrives = React.useMemo((): DriveInfo[] => {
    if (!crawlerPreferenceData?.preferences || !hasDriveMetadata) {
      return [];
    }

    const drivesMap = new Map<string, DriveInfo>();

    siteList.forEach((pref) => {
      if (pref.drive_id && pref.drive_name) {
        drivesMap.set(pref.drive_id, {
          drive_id: pref.drive_id,
          drive_name: pref.drive_name,
          drive_type: pref.drive_type,
        });
      }
    });

    return Array.from(drivesMap.values());
  }, [crawlerPreferenceData, siteList, hasDriveMetadata]);

  // Get preferences for the currently selected drive
  const currentDrivePreferences = React.useMemo(() => {
    if (!hasDriveMetadata || !selectedDriveId) {
      return preferences;
    }

    return preferences.filter((pref) => pref.drive_id === selectedDriveId);
  }, [preferences, selectedDriveId, hasDriveMetadata]);

  // Update local state when data is fetched
  React.useEffect(() => {
    if (crawlerPreferenceData?.preferences) {
      setPreferences(siteList);

      // Initialize drive selection if we have drive metadata
      if (hasDriveMetadata && availableDrives.length > 0) {
        setSelectedDriveId(availableDrives[0].drive_id as string);
      }

      // Set first tab as active by default
      const prefsToUse = hasDriveMetadata
        ? siteList.filter(
            (pref) => pref.drive_id === availableDrives[0]?.drive_id,
          )
        : siteList;
      if (prefsToUse.length > 0 && !activeTab) {
        setActiveTab(prefsToUse[0].type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crawlerPreferenceData, hasDriveMetadata, availableDrives]);

  // Update active tab when drive selection changes (but preserve current tab if it exists in new drive)
  React.useEffect(() => {
    if (currentDrivePreferences.length > 0) {
      // Only reset to first tab if current activeTab doesn't exist in the new drive's preferences
      const tabExistsInCurrentDrive = currentDrivePreferences.some(
        (pref) => pref.type === activeTab,
      );
      if (!tabExistsInCurrentDrive) {
        setActiveTab(currentDrivePreferences[0].type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriveId]);

  // Handle individual item toggle
  const handleItemToggle = React.useCallback(
    (preferenceType: string, sourceId: string) => {
      setPreferences((prev) =>
        prev.map((pref) => {
          // For drives, we need to match both type and drive_id
          const isMatch = hasDriveMetadata
            ? pref.type === preferenceType && pref.drive_id === selectedDriveId
            : pref.type === preferenceType;

          return isMatch
            ? {
                ...pref,
                data: pref.data.map((item) =>
                  item.source_id === sourceId && item.active !== false
                    ? { ...item, status: !item.status }
                    : item,
                ),
              }
            : pref;
        }),
      );
    },
    [hasDriveMetadata, selectedDriveId],
  );

  // Handle select all for a specific tab
  const handleSelectAll = React.useCallback(
    (preferenceType: string) => {
      setPreferences((prev) => {
        // Find the current preference, considering drive if applicable
        const currentPreference = hasDriveMetadata
          ? prev.find(
              (p) =>
                p.type === preferenceType && p.drive_id === selectedDriveId,
            )
          : prev.find((p) => p.type === preferenceType);

        if (!currentPreference) return prev;

        const hasAnySelected = currentPreference.data.some(
          (item) => item.active !== false && item.status,
        );
        const newStatus = !hasAnySelected;

        return prev.map((pref) => {
          const isMatch = hasDriveMetadata
            ? pref.type === preferenceType && pref.drive_id === selectedDriveId
            : pref.type === preferenceType;

          return isMatch
            ? {
                ...pref,
                data: pref.data.map((item) =>
                  item.active === false ? item : { ...item, status: newStatus },
                ),
              }
            : pref;
        });
      });
    },
    [hasDriveMetadata, selectedDriveId],
  );

  // Get tab statistics — inactive items (active === false) are excluded
  const getTabStats = React.useCallback(
    (preferenceType: string) => {
      const preference = hasDriveMetadata
        ? preferences.find(
            (p) => p.type === preferenceType && p.drive_id === selectedDriveId,
          )
        : preferences.find((p) => p.type === preferenceType);

      if (!preference) return { selected: 0, total: 0 };

      const selected = preference.data.filter((item) => item.status).length;
      const total = preference.data.length;
      return { selected, total };
    },
    [preferences, hasDriveMetadata, selectedDriveId],
  );

  // Get filtered items for current tab
  const getFilteredItems = React.useCallback(
    (items: CrawlerPreferenceItem[], query: string = searchQuery) => {
      return items.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase()),
      );
    },
    [searchQuery],
  );

  const preferencesForSave = React.useMemo(
    () => fromSiteList(preferences, groups),
    [preferences, groups],
  );

  return {
    preferences,
    preferencesForSave,
    setPreferences,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    handleItemToggle,
    handleSelectAll,
    getTabStats,
    getFilteredItems,
    // Drive-related state and data
    hasDriveMetadata,
    availableDrives,
    selectedDriveId,
    setSelectedDriveId,
    currentDrivePreferences,
  };
}
